import React from "react";
import { Box, Typography } from "@mui/material";
import { animated } from "@react-spring/web";
import {
  getViewerPanelPaddingSx,
  viewerPanelSurfaceSx,
} from "./panelStyles";
import { GestureCue } from "./ViewerGestureTutorial";

const StatusOverlays = ({
  unityBuildVersion,
  envLabelBottom,
  playerOffset,
  overlayWidth,
  guideInstructionText,
  isTouchTutorialDevice,
  zoomTutorialVisible,
  isPhone,
  currentGameState,
  lastUnitySelectedElementName,
  lastUnitySelectedElementId,
}) => {
  const SHOW_GAME_STATE_PANEL = false;
  const normalizedInstructionText =
    typeof guideInstructionText === "string" ? guideInstructionText.trim() : "";
  const instructionBottom = `calc(${envLabelBottom} + ${
    isPhone ? 86 : 104
  }px)`;
  const overlayPanelPaddingSx = getViewerPanelPaddingSx(isPhone);

  const mutedLabelSx = {
    display: "block",
    opacity: 0.9,
    letterSpacing: "0.11em",
    textTransform: "uppercase",
    color: "rgba(161, 241, 224, 0.95)",
    fontWeight: 600,
  };
  const compactOverlayHeadingSx = {
    display: "block",
    opacity: 0.72,
    letterSpacing: "0.11em",
    textTransform: "uppercase",
    color: "rgba(161, 241, 224, 0.92)",
    fontWeight: 700,
    fontSize: isPhone ? "0.68rem" : "0.74rem",
    lineHeight: 1.2,
  };
  const compactOverlayBodySx = {
    mt: 0.65,
    textAlign: "center",
    fontSize: isPhone ? "0.92rem" : "0.95rem",
    lineHeight: 1.55,
    color: "rgba(242, 255, 252, 0.88)",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  const zoomTutorialLabel = isTouchTutorialDevice
    ? "Pinch or tap zoom"
    : "Scroll or click zoom";

  return (
    <>
      {unityBuildVersion && (
        <Box
          sx={{
            position: "absolute",
            right: "calc(env(safe-area-inset-right, 0px) + 12px)",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            color: "#ecfffb",
            ...overlayPanelPaddingSx,
            zIndex: 1003,
            pointerEvents: "none",
            textAlign: "right",
            maxWidth: isPhone ? "62vw" : "360px",
            ...viewerPanelSurfaceSx,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...mutedLabelSx, fontSize: "0.64rem", letterSpacing: "0.1em" }}
          >
            UNITY VERSION
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"area-extended", "area-normal", sans-serif',
              fontSize: isPhone ? "0.78rem" : "0.86rem",
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {unityBuildVersion}
          </Typography>
        </Box>
      )}

      {normalizedInstructionText && (
        <Box
          component={animated.div}
          sx={{
            position: "absolute",
            bottom: instructionBottom,
            left: `calc(${playerOffset}px + 50% - ${playerOffset / 2}px)`,
            transform: "translateX(-50%)",
            color: "#f2fffc",
            width: "fit-content",
            maxWidth: overlayWidth,
            ...overlayPanelPaddingSx,
            boxSizing: "border-box",
            opacity: 1,
            zIndex: 26,
            pointerEvents: "none",
            textAlign: "center",
            ...viewerPanelSurfaceSx,
          }}
        >
          <Typography
            variant="caption"
            sx={compactOverlayHeadingSx}
          >
            Instruction
          </Typography>
          {zoomTutorialVisible && (
            <Box
              data-testid="guide-zoom-tutorial"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.9,
                mt: 0.7,
                px: isPhone ? 1.2 : 1.4,
                py: isPhone ? 0.45 : 0.5,
                borderRadius: "999px",
                color: "rgba(242, 255, 252, 0.96)",
                background: "rgba(5, 16, 25, 0.46)",
                border: "1px solid rgba(133, 245, 236, 0.2)",
              }}
            >
              <GestureCue step="zoom" />
              <Typography
                sx={{
                  fontSize: isPhone ? "0.78rem" : "0.84rem",
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: "inherit",
                }}
              >
                {zoomTutorialLabel}
              </Typography>
            </Box>
          )}
          <Typography
            sx={{
              ...compactOverlayBodySx,
              maxHeight: isPhone ? "7.8em" : "9.4em",
              overflowY: "auto",
            }}
          >
            {normalizedInstructionText}
          </Typography>
        </Box>
      )}

      {SHOW_GAME_STATE_PANEL && (
        <Box
          sx={{
            position: "absolute",
            top: `calc(var(--app-header-height, 0px) + 12px)`,
            right: "12px",
            color: "#fff",
            zIndex: 30,
            pointerEvents: "none",
            textAlign: "right",
            maxWidth: isPhone ? "62vw" : "380px",
            ...overlayPanelPaddingSx,
            ...viewerPanelSurfaceSx,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...mutedLabelSx, letterSpacing: "0.11em" }}
          >
            GAME STATE
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"area-extended", "area-normal", sans-serif',
              fontSize: isPhone ? "0.8rem" : "0.9rem",
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {currentGameState}
          </Typography>
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: "1px solid rgba(255, 255, 255, 0.12)",
            }}
          >
            <Typography
              variant="caption"
              sx={{ ...mutedLabelSx, letterSpacing: "0.11em" }}
            >
              LAST UNITY ELEMENT
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: '"area-extended", "area-normal", sans-serif',
                fontSize: isPhone ? "0.8rem" : "0.9rem",
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {lastUnitySelectedElementName || "None"}
            </Typography>
            {lastUnitySelectedElementId &&
              lastUnitySelectedElementId !== lastUnitySelectedElementName && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.35,
                    color: "rgba(236, 255, 251, 0.68)",
                    fontSize: isPhone ? "0.64rem" : "0.68rem",
                    lineHeight: 1.2,
                    wordBreak: "break-all",
                  }}
                >
                  {lastUnitySelectedElementId}
                </Typography>
              )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default StatusOverlays;
